import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';

const isClosedForecast = (forecast) => {
  const normalized = String(forecast || '').trim();
  return normalized === '100' || normalized === '100%' || normalized === '100.0' || parseInt(normalized, 10) === 100;
};

const getMonthKeys = (count) => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < count; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return months;
};

/**
 * LeadyModal — obchodník si zde zapíná/vypíná viditelnost šarží s selfManage=true
 * a vidí svou pozici v round-robin frontě pro auto-assign.
 */
export default function LeadyModal({ currentUserId, isDarkMode, onClose, onVisibilityChanged }) {
  const [selfManageBatches, setSelfManageBatches] = useState([]); // šarže kde admin povolil selfManage
  const [myVisibleBatches, setMyVisibleBatches] = useState([]);   // šarže co má uživatel zapnuté
  const [queuePositions, setQueuePositions] = useState({});        // { batchName: { position, total, isNext } }
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [togglingBatch, setTogglingBatch] = useState(null); // batchName právě ukládané
  const [personalStats, setPersonalStats] = useState({
    assignedThisMonth: 0,
    assignedThisMonthByBatch: {},
    mySuccessRateByBatch6m: {},
    bestSuccessRateByBatch6m: {},
    mySuccessRate6m: 0,
    bestSuccessRate: 0
  });
  const [myLeadsThisMonth, setMyLeadsThisMonth] = useState([]);  // pole leadů přidělených tento měsíc
  const [showMyLeads, setShowMyLeads] = useState(false);
  const [myComplaints, setMyComplaints] = useState([]);           // reklamace leadů podané tímto uživatelem
  const [complaintsLoading, setComplaintsLoading] = useState(true);
  const [complaintFilter, setComplaintFilter] = useState('30');   // '30' = posledních 30 dnů, 'older' = starší, 'all' = vše

  const loadMyComplaints = useCallback(async () => {
    if (!currentUserId) return;
    setComplaintsLoading(true);
    try {
      const res = await fetch(`${API_URL}/lead-complaints?userId=${currentUserId}`);
      const data = res.ok ? await res.json() : { complaints: [] };
      setMyComplaints(Array.isArray(data.complaints) ? data.complaints : []);
    } catch {
      setMyComplaints([]);
    } finally {
      setComplaintsLoading(false);
    }
  }, [currentUserId]);

  useEffect(() => { loadMyComplaints(); }, [loadMyComplaints]);

  const loadPersonalStats = useCallback(async (allowedBatches = []) => {
    if (!currentUserId) return;

    setStatsLoading(true);
    try {
      const months = getMonthKeys(6);
      const monthResults = await Promise.all(
        months.map(async (monthKey) => {
          const [year, month] = monthKey.split('-');
          const res = await fetch(
            `${API_URL}/leads-assignments?month=${parseInt(month, 10)}&year=${parseInt(year, 10)}&userId=${currentUserId || ''}`
          );
          const data = res.ok ? await res.json() : [];
          return { monthKey, data: Array.isArray(data) ? data : [] };
        })
      );

      const userStats = new Map(); // userId -> { total, closed }
      let assignedThisMonth = 0;
      const assignedThisMonthByBatch = {};
      const myBatchStats = new Map(); // batchName -> { total, closed }
      const batchUserStats = new Map(); // batchName -> Map(userId -> { total, closed })

      monthResults.forEach(({ monthKey, data }, index) => {
        data
          .filter(item => allowedBatches.length === 0 || allowedBatches.includes(item.batchName))
          .forEach(item => {
            const uid = String(item.toUserId || item.userId || item.toUserName || 'unknown');
            const closed = isClosedForecast(item.forecast);

            if (!userStats.has(uid)) {
              userStats.set(uid, { total: 0, closed: 0 });
            }

            const stat = userStats.get(uid);
            stat.total += 1;
            if (closed) stat.closed += 1;

            // index 0 je aktuální měsíc
            if (index === 0 && uid === String(currentUserId)) {
              assignedThisMonth += 1;
              const batchName = item.batchName || 'Nezařazeno';
              assignedThisMonthByBatch[batchName] = (assignedThisMonthByBatch[batchName] || 0) + 1;
            }

            if (uid === String(currentUserId)) {
              const batchName = item.batchName || 'Nezařazeno';
              if (!myBatchStats.has(batchName)) {
                myBatchStats.set(batchName, { total: 0, closed: 0 });
              }
              const batchStat = myBatchStats.get(batchName);
              batchStat.total += 1;
              if (closed) batchStat.closed += 1;
            }

            const batchName = item.batchName || 'Nezařazeno';
            if (!batchUserStats.has(batchName)) {
              batchUserStats.set(batchName, new Map());
            }
            const usersInBatch = batchUserStats.get(batchName);
            if (!usersInBatch.has(uid)) {
              usersInBatch.set(uid, { total: 0, closed: 0 });
            }
            const batchUserStat = usersInBatch.get(uid);
            batchUserStat.total += 1;
            if (closed) batchUserStat.closed += 1;
          });
      });

      const ranking = Array.from(userStats.entries())
        .filter(([, stat]) => stat.total > 0)
        .map(([uid, stat]) => ({
          uid,
          total: stat.total,
          closed: stat.closed,
          rate: stat.closed / stat.total
        }))
        .sort((a, b) => {
          if (a.rate !== b.rate) return b.rate - a.rate;
          return b.total - a.total;
        });

      const myStats = userStats.get(String(currentUserId)) || { total: 0, closed: 0 };
      const mySuccessRateByBatch6m = {};
      const bestSuccessRateByBatch6m = {};
      myBatchStats.forEach((stat, batchName) => {
        mySuccessRateByBatch6m[batchName] = {
          rate: stat.total > 0 ? Math.round((stat.closed / stat.total) * 100) : 0,
          total: stat.total,
          closed: stat.closed
        };
      });

      batchUserStats.forEach((usersInBatch, batchName) => {
        let bestRate = 0;
        let bestTotal = 0;
        let bestClosed = 0;
        usersInBatch.forEach((stat) => {
          if (stat.total <= 0) return;
          const rate = stat.closed / stat.total;
          if (rate > bestRate || (rate === bestRate && stat.total > bestTotal)) {
            bestRate = rate;
            bestTotal = stat.total;
            bestClosed = stat.closed;
          }
        });
        bestSuccessRateByBatch6m[batchName] = {
          rate: Math.round(bestRate * 100),
          total: bestTotal,
          closed: bestClosed
        };
      });

      const mySuccessRate6m = myStats.total > 0 ? Math.round((myStats.closed / myStats.total) * 100) : 0;
      const bestSuccessRate = ranking.length > 0 ? Math.round(ranking[0].rate * 100) : 0;

      // Leady přidělené aktuálnímu uživateli tento měsíc
      const currentMonthData = (monthResults[0]?.data || []).filter(
        item => String(item.toUserId) === String(currentUserId)
      );
      setMyLeadsThisMonth(currentMonthData);

      setPersonalStats({
        assignedThisMonth,
        assignedThisMonthByBatch,
        mySuccessRateByBatch6m,
        bestSuccessRateByBatch6m,
        mySuccessRate6m,
        bestSuccessRate
      });
    } catch (err) {
      console.error('LeadyModal personal stats error:', err);
      setPersonalStats({
        assignedThisMonth: 0,
        assignedThisMonthByBatch: {},
        mySuccessRateByBatch6m: {},
        bestSuccessRateByBatch6m: {},
        mySuccessRate6m: 0,
        bestSuccessRate: 0
      });
    } finally {
      setStatsLoading(false);
    }
  }, [currentUserId]);

  const loadData = useCallback(async () => {
    if (!currentUserId) return;
    try {
      const [smRes, visRes, queueRes] = await Promise.all([
        fetch(`${API_URL}/batch-autoassign/selfmanage?userId=${currentUserId}`),
        fetch(`${API_URL}/batch-visibility/${currentUserId}`),
        fetch(`${API_URL}/batch-autoassign/queue-position?userId=${currentUserId}`),
      ]);
      const [smData, visData, queueData] = await Promise.all([
        smRes.ok ? smRes.json() : { batches: [] },
        visRes.ok ? visRes.json() : { visibleBatches: [] },
        queueRes.ok ? queueRes.json() : { positions: {} },
      ]);
      const allowedBatches = smData.batches || [];
      setSelfManageBatches(allowedBatches);
      setMyVisibleBatches(visData.visibleBatches || []);
      setQueuePositions(queueData.positions || {});
      await loadPersonalStats(allowedBatches);
    } catch (e) {
      console.error('LeadyModal loadData error:', e);
    } finally {
      setLoading(false);
    }
  }, [currentUserId, loadPersonalStats]);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleBatch = async (batchName) => {
    if (togglingBatch) return; // zabraň dvojitému kliknutí
    const newVisible = myVisibleBatches.includes(batchName)
      ? myVisibleBatches.filter(b => b !== batchName)
      : [...myVisibleBatches, batchName];

    // Optimistická aktualizace UI a vymazání pozice pro tuto šarži
    setMyVisibleBatches(newVisible);
    setQueuePositions(prev => {
      const next = { ...prev };
      delete next[batchName];
      return next;
    });

    // Uložit na server a načíst nové pořadí
    setTogglingBatch(batchName);
    try {
      const visRes = await fetch(`${API_URL}/batch-visibility/${currentUserId}`);
      const visData = visRes.ok ? await visRes.json() : { visibleBatches: [] };
      const adminManaged = (visData.visibleBatches || []).filter(b => !selfManageBatches.includes(b));
      const merged = [...new Set([...adminManaged, ...newVisible.filter(b => selfManageBatches.includes(b))])];
      await fetch(`${API_URL}/batch-visibility/${currentUserId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visibleBatches: merged }),
      });
      // Načti aktualizované pořadí
      const queueRes = await fetch(`${API_URL}/batch-autoassign/queue-position?userId=${currentUserId}`);
      const queueData = queueRes.ok ? await queueRes.json() : { positions: {} };
      setQueuePositions(queueData.positions || {});
      if (onVisibilityChanged) onVisibilityChanged();
    } catch (e) {
      console.error('Toggle batch error:', e);
    } finally {
      setTogglingBatch(null);
    }
  };

  const bg = isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900';
  const borderCls = isDarkMode ? 'border-gray-600' : 'border-gray-200';
  const rowBg = (i) => isDarkMode ? (i % 2 === 0 ? 'bg-gray-800' : 'bg-gray-750') : (i % 2 === 0 ? 'bg-white' : 'bg-gray-50');

  // Filtr reklamací podle stáří (posledních 30 dnů / starší / vše)
  const filteredComplaints = (() => {
    if (complaintFilter === 'all') return myComplaints;
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return myComplaints.filter((c) => {
      const t = new Date(c.complainedAt || c.createdAt || 0).getTime();
      if (Number.isNaN(t)) return complaintFilter === 'older';
      return complaintFilter === '30' ? t >= cutoff : t < cutoff;
    });
  })();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`w-[1120px] max-w-[97vw] max-h-[85vh] flex flex-col rounded-xl shadow-2xl ${bg}`}>
        {/* Hlavička */}
        <div className={`p-4 border-b ${borderCls} flex justify-between items-center`}>
          <div>
            <h2 className="text-lg font-bold">🎯 Leady — moje šarže</h2>
            <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Zapni si přijímání leadů ze dostupných šarží.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none">✕</button>
        </div>

        {/* Obsah — rozdělený na dvě půlky */}
        <div className="flex-1 overflow-y-auto p-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* LEVÁ půlka — šarže, přijímání leadů, pořadí, měsíční leady */}
          <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent" />
            </div>
          ) : selfManageBatches.length === 0 ? (
            <div className="text-center py-10">
              <div className="text-5xl mb-3">📭</div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Zatím nejsou k dispozici žádné šarže.
              </p>
            </div>
          ) : (
            <div className={`rounded-lg border overflow-hidden ${borderCls}`}>
              <table className="min-w-full">
                <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase">Šarže</th>
                    <th className="px-4 py-2 text-center text-xs font-semibold uppercase">Přijímat leady</th>
                    <th className="px-4 py-2 text-center text-xs font-semibold uppercase">Pořadí</th>
                  </tr>
                </thead>
                <tbody>
                  {selfManageBatches.map((batchName, i) => {
                    const enabled = myVisibleBatches.includes(batchName);
                    const pos = queuePositions[batchName];
                    const assignedCount = personalStats.assignedThisMonthByBatch?.[batchName] || 0;
                    return (
                      <tr key={batchName} className={rowBg(i)}>
                        <td className="px-4 py-3 text-sm font-medium">{batchName}</td>
                        <td className="px-4 py-3 text-center">
                          <div
                            className={`inline-flex items-center gap-2 ${togglingBatch === batchName ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
                            onClick={() => toggleBatch(batchName)}
                          >
                            <div
                              className={`relative w-10 h-5 rounded-full transition-colors shrink-0 ${enabled ? 'bg-green-500' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}
                            >
                              <span className={`absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${enabled ? 'translate-x-[22px]' : 'translate-x-0'}`} />
                            </div>
                            <span
                              className={`text-xs font-semibold shrink-0 ${enabled ? 'text-green-600' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
                              style={{ minWidth: '2rem', display: 'inline-block' }}
                            >
                              {togglingBatch === batchName ? '…' : enabled ? 'ZAP' : 'VYP'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {!enabled ? (
                            <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>—</span>
                          ) : togglingBatch === batchName ? (
                            <span className="text-xs text-gray-400">načítám…</span>
                          ) : pos.isNext ? (
                            <div className="flex flex-col items-center gap-1">
                              <span className="text-xs font-bold text-green-600 dark:text-green-400">🎯 Jsem příští!</span>
                              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Přiděleno: {assignedCount}</span>
                              {pos.mode === 'fewest-leads' && (
                                <span className="text-[10px] text-amber-600 dark:text-amber-400" title="Leady se přidělují tomu, kdo má nejméně">⚖️ dle nejméně leadů</span>
                              )}
                            </div>
                          ) : (
                            <div className="flex flex-col items-center gap-1 text-xs">
                              <span>
                                <span className={`font-semibold ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>{pos.position}.</span>
                                <span className={`ml-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>z {pos.total}</span>
                              </span>
                              <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Přiděleno: {assignedCount}</span>
                              {pos.mode === 'fewest-leads' && (
                                <span className="text-[10px] text-amber-600 dark:text-amber-400" title="Leady se přidělují tomu, kdo má nejméně">⚖️ dle nejméně leadů</span>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Leady přidělené tento měsíc ─────────────────────────── */}
          {!loading && (
            <div className={`rounded-lg border overflow-hidden ${borderCls}`}>
              {/* Záhlaví sekce */}
              <button
                type="button"
                className={`w-full flex items-center justify-between px-4 py-3 text-left font-semibold text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-650' : 'bg-gray-50 hover:bg-gray-100'} transition-colors`}
                onClick={() => setShowMyLeads(v => !v)}
              >
                <span>
                  📋 Leady přidělené tento měsíc
                  {!statsLoading && (
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${isDarkMode ? 'bg-blue-700 text-blue-200' : 'bg-blue-100 text-blue-700'}`}>
                      {myLeadsThisMonth.length}
                    </span>
                  )}
                </span>
                <span className="text-gray-400">{showMyLeads ? '▲' : '▼'}</span>
              </button>

              {showMyLeads && (
                statsLoading ? (
                  <div className="px-4 py-6 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent mx-auto" />
                  </div>
                ) : myLeadsThisMonth.length === 0 ? (
                  <div className={`px-4 py-6 text-center text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Tento měsíc zatím žádné leady.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold uppercase">Firma</th>
                          <th className="px-3 py-2 text-left font-semibold uppercase">IČO</th>
                          <th className="px-3 py-2 text-left font-semibold uppercase">Šarže</th>
                          <th className="px-3 py-2 text-left font-semibold uppercase">Datum</th>
                          <th className="px-3 py-2 text-left font-semibold uppercase">Stav</th>
                          <th className="px-3 py-2 text-left font-semibold uppercase">FC</th>
                        </tr>
                      </thead>
                      <tbody>
                        {myLeadsThisMonth.map((lead, i) => (
                          <tr key={lead.transferId || i} className={rowBg(i)}>
                            <td className="px-3 py-2 font-medium max-w-[160px] truncate" title={lead.companyName}>
                              {lead.companyName || '—'}
                            </td>
                            <td className="px-3 py-2">{lead.companyIco || '—'}</td>
                            <td className={`px-3 py-2 max-w-[120px] truncate ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`} title={lead.batchName}>
                              {lead.batchName || '—'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {lead.transferDate ? new Date(lead.transferDate).toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit' }) : '—'}
                            </td>
                            <td className="px-3 py-2">{lead.status || '—'}</td>
                            <td className={`px-3 py-2 font-semibold ${lead.forecast === '100' || lead.forecast === '100%' ? 'text-green-600 dark:text-green-400' : ''}`}>
                              {lead.forecast || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </div>
          )}

          </div>
          {/* /LEVÁ půlka */}

          {/* PRAVÁ půlka — reklamace leadů */}
          <div className="space-y-3">
            <div className={`rounded-lg border overflow-hidden ${borderCls}`}>
              <div className={`px-4 py-3 font-semibold text-sm ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} flex items-center justify-between`}>
                <span>🚩 Moje reklamace leadů{!complaintsLoading && (
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${isDarkMode ? 'bg-red-800 text-red-200' : 'bg-red-100 text-red-700'}`}>{filteredComplaints.length}</span>
                )}</span>
                <button type="button" onClick={loadMyComplaints} className="text-xs text-blue-500 hover:underline">obnovit</button>
              </div>
              {/* Filtr podle stáří */}
              <div className={`px-4 py-2 flex gap-1.5 border-b ${borderCls} ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                {[
                  { key: '30', label: 'Posledních 30 dnů' },
                  { key: 'older', label: 'Starší' },
                  { key: 'all', label: 'Vše' }
                ].map(opt => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setComplaintFilter(opt.key)}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition ${complaintFilter === opt.key ? 'bg-blue-600 text-white' : (isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {complaintsLoading ? (
                <div className="px-4 py-6 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent mx-auto" />
                </div>
              ) : filteredComplaints.length === 0 ? (
                <div className={`px-4 py-8 text-center text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {myComplaints.length === 0
                    ? 'Zatím jste nepodali žádnou reklamaci leadu.'
                    : 'Žádné reklamace v tomto filtru.'}
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[420px] overflow-y-auto">
                  {filteredComplaints.map((c) => {
                    const statusMap = {
                      'open': { label: 'Nová', cls: isDarkMode ? 'bg-yellow-800 text-yellow-100' : 'bg-yellow-100 text-yellow-700' },
                      'in-progress': { label: 'V řešení', cls: isDarkMode ? 'bg-blue-800 text-blue-100' : 'bg-blue-100 text-blue-700' },
                      'approved': { label: 'Uznáno', cls: isDarkMode ? 'bg-green-800 text-green-100' : 'bg-green-100 text-green-700' },
                      'rejected': { label: 'Neuznáno', cls: isDarkMode ? 'bg-red-800 text-red-100' : 'bg-red-100 text-red-700' },
                    };
                    const st = statusMap[c.status] || statusMap['open'];
                    return (
                      <div key={c._id} className="px-4 py-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-medium text-sm truncate" title={c.companyName}>{c.companyName || '—'}</div>
                            {c.ico && <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>IČ: {c.ico}</div>}
                          </div>
                          <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-semibold ${st.cls}`}>{st.label}</span>
                        </div>
                        {c.description && (
                          <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>💬 {c.description}</div>
                        )}
                        {c.adminReply && (
                          <div className={`text-xs mt-1 p-2 rounded ${isDarkMode ? 'bg-gray-700 text-green-300' : 'bg-green-50 text-green-700'}`}>
                            ↩ Odpověď callcentra: {c.adminReply}
                          </div>
                        )}
                        <div className={`text-[10px] mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          Reklamováno: {c.complainedAt ? new Date(c.complainedAt).toLocaleString('cs-CZ') : '—'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          {/* /PRAVÁ půlka */}

        </div>

        {/* Patička */}
        <div className={`p-4 border-t ${borderCls} flex justify-end`}>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg text-sm font-medium border ${borderCls} ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
          >
            Zavřít
          </button>
        </div>
      </div>
    </div>
  );
}
